import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatBasisPoints, schemas, type TdsSectionMaster } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useTdsSections } from '@/hooks/useTdsSections';
import { Modal, ToneBadge } from '@/components/ui';
import { describeError } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { CrudPage } from './CrudPage';

/**
 * The tenant's TDS sections and the rates each is deducted at, shared by all
 * its companies. Starts with the common sections prebuilt; the tax team edits
 * them, adds sections and switches off the ones it never uses. Invoices and
 * vendor TDS profiles pick a section and one of its rates from here.
 *
 * When the law renumbers a section (194IB becoming 194IC), the section is
 * replaced rather than edited: the new code takes its rules over and the old
 * one stays, read-only, under the new one's Previous versions.
 */
export function TdsSectionsPage() {
  const { can } = useAuth();
  const { all } = useTdsSections();
  const [replacing, setReplacing] = useState<TdsSectionMaster | null>(null);

  return (
    <>
      <CrudPage<TdsSectionMaster>
        title="TDS Sections"
        subtitle="Sections of the Income-tax Act and the rates each is deducted at — shared by every company. Invoices and vendor profiles pick from this list."
        queryKey="settings-tds-sections"
        permissions={{
          read: 'tds_section:read',
          create: 'tds_section:create',
          update: 'tds_section:update',
          delete: 'tds_section:delete',
        }}
        list={(query) => api.settings.tdsSections({ ...query, includeReplaced: 'true' })}
        create={(body) => api.settings.createTdsSection(body)}
        update={(id, body) => api.settings.updateTdsSection(id, body)}
        remove={(id) => api.settings.deleteTdsSection(id)}
        restore={(id) => api.settings.updateTdsSection(id, { active: true })}
        schemas={{
          create: schemas.createTdsSectionRequest,
          update: schemas.updateTdsSectionRequest,
        }}
        columns={[
          {
            header: 'Section',
            render: (row) => (
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-xs text-ink-500">{row.code}</span>
                <span className={row.replacedBy ? 'text-ink-500' : 'font-medium'}>{row.name}</span>
                {row.replacedBy ? (
                  <ToneBadge tone="neutral">Replaced by {row.replacedBy.code}</ToneBadge>
                ) : row.replaces ? (
                  <ToneBadge tone="progress">Replaces {row.replaces.code}</ToneBadge>
                ) : null}
              </span>
            ),
          },
          {
            header: 'Rates',
            render: (row) => (
              <span className="flex flex-wrap gap-1.5">
                {row.rates.map((rate) => (
                  <ToneBadge key={rate.rateBasisPoints} tone="neutral">
                    {formatBasisPoints(rate.rateBasisPoints)} · {rate.label}
                  </ToneBadge>
                ))}
              </span>
            ),
          },
        ]}
        fields={[
          {
            name: 'code',
            label: 'Section',
            required: true,
            help: 'As the Act numbers it, e.g. 194C or 194IA. When the law renumbers a section, use Replace with new section instead.',
          },
          { name: 'name', label: 'What it covers', required: true },
          {
            name: 'rates',
            label: 'Rates',
            type: 'tdsRates',
            required: true,
            help: 'Each rate this section is deducted at, and who or what it applies to. Only these are offered on invoices.',
          },
        ]}
        defaults={{ rates: [{ label: '', rateBasisPoints: 0 }] }}
        formColumns={2}
        formActions={(row) =>
          !row.replacedBy && row.active && can('tds_section:update')
            ? [
                {
                  label: 'Replace with new section',
                  closesForm: true,
                  run: async () => setReplacing(row),
                },
              ]
            : []
        }
        formReadOnly={(row) => !!row.replacedBy}
        formNotice={(row) =>
          row.replacedBy ? (
            <p className="notice-warning text-sm">
              Replaced by <strong>{row.replacedBy.code}</strong> on {formatDate(row.replacedBy.at)}
              {row.replacedBy.byName ? ` by ${row.replacedBy.byName}` : ''}. Kept for reference
              only: invoices and vendor profiles now use {row.replacedBy.code}.
            </p>
          ) : null
        }
        formFooter={(row) => <PreviousVersions row={row} all={all} />}
      />
      {replacing ? (
        <ReplaceSectionModal section={replacing} onClose={() => setReplacing(null)} />
      ) : null}
    </>
  );
}

/** The codes this section took over, newest first, each as it last stood. */
function previousVersionsOf(row: TdsSectionMaster, all: TdsSectionMaster[]): TdsSectionMaster[] {
  const versions: TdsSectionMaster[] = [];
  let at = row.replaces ? all.find((section) => section.id === row.replaces!.id) : undefined;
  while (at && versions.length < 20) {
    versions.push(at);
    const previous: TdsSectionMaster['replaces'] = at.replaces;
    at = previous ? all.find((section) => section.id === previous.id) : undefined;
  }
  return versions;
}

function PreviousVersions({ row, all }: { row: TdsSectionMaster; all: TdsSectionMaster[] }) {
  const versions = previousVersionsOf(row, all);
  if (!versions.length) return null;
  return (
    <section
      aria-label="Previous versions"
      className="rounded-lg border border-ink-100 bg-ink-50/60"
    >
      <h3 className="border-b border-ink-100 px-3 py-2 text-sm font-semibold text-ink-800">
        Previous versions
      </h3>
      <ul className="divide-y divide-ink-100 text-sm">
        {versions.map((version) => (
          <li key={version.id} className="space-y-1 px-3 py-2">
            <p>
              <span className="font-mono text-xs text-ink-500">{version.code}</span>{' '}
              <span className="text-ink-800">{version.name}</span>
            </p>
            <p className="flex flex-wrap gap-1.5">
              {version.rates.map((rate) => (
                <ToneBadge key={rate.rateBasisPoints} tone="neutral">
                  {formatBasisPoints(rate.rateBasisPoints)} · {rate.label}
                </ToneBadge>
              ))}
            </p>
            {version.replacedBy ? (
              <p className="text-xs text-ink-500">
                Replaced by {version.replacedBy.code} on {formatDate(version.replacedBy.at)}
                {version.replacedBy.byName ? ` by ${version.replacedBy.byName}` : ''}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The new code taking a section over. Its name and rates carry over; the
 * old section is kept, read-only, as a previous version.
 */
function ReplaceSectionModal({ section, onClose }: { section: TdsSectionMaster; onClose(): void }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState(section.name);
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [done, setDone] = useState<{
    code: string;
    vendors: number;
    invoices: number;
    loans: number;
  } | null>(null);

  const replace = useMutation({
    mutationFn: () =>
      api.settings.replaceTdsSection(section.id, {
        code: code.trim().toUpperCase(),
        ...(name.trim() && name.trim() !== section.name ? { name: name.trim() } : {}),
        effectiveFrom,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['settings-tds-sections'] });
      setDone({ code: result.code, ...result.moved });
    },
  });

  if (done) {
    return (
      <Modal
        title={`${section.code} replaced by ${done.code}`}
        onClose={onClose}
        footer={
          <button type="button" className="btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <div className="space-y-2 text-sm text-ink-700">
          <p>
            {done.code} now carries {section.code}’s rules. {section.code} is kept, read-only, under{' '}
            {done.code}’s Previous versions.
          </p>
          <p>
            Moved to {done.code}: {done.vendors} vendor{done.vendors === 1 ? '' : 's'},{' '}
            {done.invoices} open invoice{done.invoices === 1 ? '' : 's'}, {done.loans} loan
            {done.loans === 1 ? '' : 's'}. Invoices already verified keep {section.code}.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Replace ${section.code}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!code.trim() || replace.isPending}
            onClick={() => replace.mutate()}
          >
            {replace.isPending ? 'Replacing…' : `Replace ${section.code}`}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          For a section the law has renumbered. The new section takes over {section.code}’s rates;{' '}
          {section.code} is kept, read-only, for reference. Vendor TDS profiles, invoices not yet
          verified and running loans move to the new section; anything already verified keeps{' '}
          {section.code}.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">
              New section<span className="text-red-600"> *</span>
            </span>
            <input
              className="input font-mono"
              value={code}
              placeholder="e.g. 194IC"
              onChange={(event) => setCode(event.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Applies from</span>
            <input
              className="input"
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="label">What it covers</span>
            <input
              className="input"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        </div>
        <div>
          <span className="label">Rates carried over</span>
          <p className="flex flex-wrap gap-1.5">
            {section.rates.map((rate) => (
              <ToneBadge key={rate.rateBasisPoints} tone="neutral">
                {formatBasisPoints(rate.rateBasisPoints)} · {rate.label}
              </ToneBadge>
            ))}
          </p>
          <p className="mt-1 text-xs text-ink-500">
            Change them on the new section afterwards if the law changed them too.
          </p>
        </div>
        {replace.error ? (
          <p className="text-sm text-red-700">{describeError(replace.error)}</p>
        ) : null}
      </div>
    </Modal>
  );
}
